import PromoService from '@/services/PromoService'
import moment from 'moment-timezone'

const defaultPromo = () => ({
  _id: undefined,
  title: '',
  description: '',
  companies: [],
  locations: [],
  nickname: undefined,
  inStorePromoCode: '',
  inStorePromoFile: '',
  offPremisePromoCode: '',
  offPremisePromoFile: '',
  offPremiseLocationUrl: '',
  showInStoreQrCode: false,
  expiresAt: undefined,
  expiresIn: undefined,
  source: '',
  termsAndConditions: undefined
})

export default {
  namespaced: true,
  state: {
    promos: [],
    promo: defaultPromo(),
    sourceFilter: 'all',
    isLoadingPromos: false
  },
  getters: {
    selectPromos: (state) => state.promos,
    selectPromo: (state) => state.promo,
    selectSourceFilter: (state) => state.sourceFilter,
    selectPromoName: (state) => (promoId) => {
      const promo = state.promos.find((p) => p._id === promoId)
      return promo ? promo.nickname || promo.title : ''
    },
    selectIsLoadingPromos: (state) => state.isLoadingPromos,
    // Backward compatibility aliases for old components
    promos: (state) => state.promos,
    promo: (state) => state.promo,
    sourceFilter: (state) => state.sourceFilter
  },
  mutations: {
    SET_SOURCE_FILTER(state, filter) {
      state.sourceFilter = filter
    },
    SET_PROMOS(state, promos) {
      state.promos = promos
    },
    SET_PROMO(state, promo) {
      state.promo = promo
    },
    RESET_PROMO(state) {
      state.promo = { ...defaultPromo() }
    },
    SET_IS_LOADING_PROMOS(state, status) {
      state.isLoadingPromos = status
    },
    // Backward compatibility aliases for old components
    resetPromo(state) {
      state.promo = { ...defaultPromo() }
    },
    setPromos(state, promos) {
      state.promos = promos
    },
    setTitle(state, title) {
      state.promo.title = title
    },
    setDescription(state, description) {
      state.promo.description = description
    },
    setLocations(state, locations) {
      state.promo.locations = locations
    },
    setExpiresAt(state, expiresAt) {
      state.promo.expiresAt = expiresAt
    },
    setExpiresIn(state, expiresIn) {
      state.promo.expiresIn = expiresIn
    },
    setInStorePromoCode(state, code) {
      state.promo.inStorePromoCode = code
    },
    setInStorePromoFile(state, file) {
      state.promo.inStorePromoFile = file
    },
    setOffPremisePromoCode(state, code) {
      state.promo.offPremisePromoCode = code
    },
    setOffPremisePromoFile(state, file) {
      state.promo.offPremisePromoFile = file
    },
    setOffPremiseLocationUrl(state, url) {
      state.promo.offPremiseLocationUrl = url
    },
    setNickname(state, nickname) {
      state.promo.nickname = nickname
    },
    setTermsAndConditions(state, termsAndConditions) {
      state.promo.termsAndConditions = termsAndConditions
    },
    setSource(state, source) {
      state.promo.source = source
    },
    setPromo(state, promo) {
      // Clear file fields when setting a promo (they should be re-uploaded if needed)
      state.promo = { ...promo, inStorePromoFile: '', offPremisePromoFile: '' }
    }
  },
  actions: {
    async formatPayload({ rootGetters }, promo) {
      const activeCompany = rootGetters['company/selectActiveCompany']
      const organization = rootGetters['organization/selectOrganization']
      const payload = {
        ...promo,
        ...(activeCompany._id === 'ALL_COMPANIES' && organization
          ? { organization: organization._id }
          : {})
      }
      payload.expiresAt = moment(promo.expiresAt).endOf('day').toISOString() || undefined
      payload.expiresIn = parseInt(promo.expiresIn) || undefined
      payload.companyIds = promo.companies.length
        ? promo.companies
        : rootGetters['company/selectRelevantCompanies'].map((c) => c._id)
      /*payload.locationIds = promo.locations.length
        ? promo.locations
        : rootGetters['location/selectLocationsByCompanies'](payload.companyIds).map((l) => l._id)*/

      // Ensure locationIds are strings, not objects
      payload.locationIds = promo.locations.length
        ? promo.locations.map((loc) => (typeof loc === 'string' ? loc : loc._id))
        : []

      // Ensure showInStoreQrCode is explicitly included (defaults to false if not set)
      payload.showInStoreQrCode = promo.showInStoreQrCode || false

      // remove fields the backend doesn't want
      delete payload.companies
      delete payload.locations

      return payload
    },

    async fetchPromos({ rootGetters, commit }) {
      commit('SET_IS_LOADING_PROMOS', true)
      const companies = rootGetters['company/selectRelevantCompanies']
      const activeCompany = rootGetters['company/selectActiveCompany']
      const organization = rootGetters['organization/selectOrganization']
      const response = await PromoService.listPromos({
        filters: {
          companyIds: companies.map((c) => c._id),
          ...(activeCompany._id === 'ALL_COMPANIES' ? { organizationIds: [organization._id] } : {})
        }
      })

      commit('SET_PROMOS', response.body.data.promos)
      commit('SET_IS_LOADING_PROMOS', false)
    },

    async createPromo({ dispatch }, { promo }) {
      const formattedPromo = await dispatch('formatPayload', { ...promo })
      return PromoService.createPromo({ promo: formattedPromo })
    },

    async fetchPromo({}) {
      // TODO: implement this when needed
      console.log('fetching single promo')
    },

    async updatePromo({ dispatch }, { promoId, promo }) {
      const formattedPromo = await dispatch('formatPayload', { ...promo })
      return PromoService.updatePromo({ promoId, promo: formattedPromo })
    },

    async deletePromo({}, promoId) {
      const response = await PromoService.deletePromo({ promoId })
    },

    async setPromo({ commit }, promo) {
      commit('SET_PROMO', promo)
    },

    async setSourceFilter({ commit }, filter) {
      commit('SET_SOURCE_FILTER', filter)
    },

    async resetPromo({ commit }) {
      commit('RESET_PROMO')
    },

    async setIsLoadingPromos({ commit }, status) {
      commit('SET_IS_LOADING_PROMOS', status)
    },

    async setNickname({ commit }, nickname) {
      commit('setNickname', nickname)
    },

    async setTermsAndConditions({ commit }, termsAndConditions) {
      commit('setTermsAndConditions', termsAndConditions)
    },

    // Backward compatibility actions for old components
    // These map to fetchPromos but with different filtering logic
    async queryPromos({ rootGetters, commit }) {
      const companies =
        rootGetters['company/activeCompany']._id == 'ALL_COMPANIES'
          ? rootGetters['company/companies']
          : [rootGetters['company/activeCompany']]
      const companyIds = companies.map((company) => company._id)
      const result = await PromoService.listPromos({
        filters: {
          companyIds
        }
      })
      commit('setPromos', result.body.data.promos)
    },

    async queryPromosWithSource({ rootState, rootGetters, commit }) {
      const companies =
        rootGetters['company/activeCompany']._id == 'ALL_COMPANIES'
          ? rootGetters['company/companies']
          : [rootGetters['company/activeCompany']]
      const companyIds = companies.map((company) => company._id)
      const sources = rootState.promo.promo.source ? [rootState.promo.promo.source] : []
      const result = await PromoService.listPromos({
        filters: {
          companyIds,
          disabled: false,
          ...(sources.length > 0 ? { sources } : {})
        }
      })
      commit('setPromos', result.body.data.promos)
    },

    async getUsedUniqueCode(_, { customerId, promoId, redemptionTimestamp }) {
      try {
        return await PromoService.usedCode({ customerId, promoId, redemptionTimestamp })
      } catch (error) {
        console.log(error)
      }
    }
  }
}
